import store from "../store";
import { flattenCompanyPortalResource } from "../network/apis/companyPortalRegistration";
import {
  adminPermissionModules,
  companyPermissionModules,
  otherRolesStatus,
} from "./Constants";
import { ROUTE_PATHS } from "./RoutesPaths";
import { permissionsArrayToMap } from "../containers/Company-portal/TeamsPermissions/roleMappers";
import { toPermissionsPayload } from "../containers/Company-portal/TeamsPermissions/CreateRole/permissionModules";

export const isAdmin = () => {
  return localStorage.getItem("userType") === "admin";
};

export const isSuperAdmin = () => {
  return store.getState().auth?.login?.role?.name_en === "Super Admin";
};

export const isLoginUser = () => {
  return store.getState().auth?.login?.is_user;
};

export const isVisitor = () => {
  return localStorage.getItem("userType") === "visitor";
};

export const rolePermissions = () => {
  return store?.getState()?.auth?.login?.role;
};

export const isUser = () => {
  return localStorage.getItem("userType") === "client";
};

export const isSupervisorManger = () => {
  return localStorage.getItem("userType") === "supervisor";
};

export const isCompanyOwner = (login) => {
  const sessionLogin = login ?? store?.getState()?.auth?.login;
  return Boolean(sessionLogin?.is_company_owner);
};

/** Flat `company_role` from session (unwraps JSON:API `attributes`). */
export const getNormalizedCompanyLoginRole = (login) => {
  const sessionLogin = login ?? store?.getState()?.auth?.login;
  const raw = sessionLogin?.company_role ?? sessionLogin?.role ?? null;
  if (!raw || typeof raw !== "object") return null;
  return flattenCompanyPortalResource(raw);
};

/** Company employee governed by company_role RBAC (not the owner). */
export const isCompanyRbacEmployee = (login) => {
  const sessionLogin = login ?? store?.getState()?.auth?.login;
  if (isCompanyOwner(sessionLogin)) return false;

  const role = getNormalizedCompanyLoginRole(sessionLogin);
  return Boolean(sessionLogin?.company_role_id ?? role?.id);
};

/** Disabled roles must behave as if the user has no module permissions. */
export const isCompanyEmployeeRoleEnabled = (login) => {
  const sessionLogin = login ?? store?.getState()?.auth?.login;
  if (isCompanyOwner(sessionLogin)) return true;

  const role = getNormalizedCompanyLoginRole(sessionLogin);
  if (!role) return true;

  if (role.active === false) return false;
  if (role.status === otherRolesStatus.disabled) return false;

  return true;
};

const deniedCompanyModulePermission = (moduleName) => ({
  module_name: moduleName,
  can_view: false,
  can_add: false,
  can_edit: false,
  can_delete: false,
});

const fullCompanyModulePermission = (moduleName) => ({
  module_name: moduleName,
  can_view: true,
  can_add: true,
  can_edit: true,
  can_delete: true,
});

/**
 * Company-portal permissions array for the logged-in user, or `null` when the
 * session carries no permissions data (e.g. company owner / legacy session).
 */
export const getCompanyLoginPermissions = (login) => {
  const role = getNormalizedCompanyLoginRole(login);
  const permissions = role?.permissions;
  return Array.isArray(permissions) && permissions.length > 0
    ? permissions
    : null;
};

/** Cache keyed by the raw permissions array identity — the engine runs once
 * per distinct array reference rather than on every `getCompanyModulePermission` call. */
const derivedCompanyPermissionsCache = new WeakMap();

/** Runs the dependency cascade over a session's stored permissions array, so
 * a role saved before the cascade existed (or written by another client)
 * still resolves the same automatic-access rules as the authoring UI. */
const deriveCompanyPermissions = (permissions) => {
  if (derivedCompanyPermissionsCache.has(permissions)) {
    return derivedCompanyPermissionsCache.get(permissions);
  }
  const derived = toPermissionsPayload(permissionsArrayToMap(permissions));
  derivedCompanyPermissionsCache.set(permissions, derived);
  return derived;
};

/** Permission row for one company module, or `null` when the session has no
 * permissions data (owner / legacy — callers treat `null` as full access). */
export const getCompanyModulePermission = (moduleName, login) => {
  const sessionLogin = login ?? store?.getState()?.auth?.login;

  if (isCompanyOwner(sessionLogin)) {
    return fullCompanyModulePermission(moduleName);
  }

  if (isCompanyRbacEmployee(sessionLogin)) {
    if (!isCompanyEmployeeRoleEnabled(sessionLogin)) {
      return deniedCompanyModulePermission(moduleName);
    }

    const permissions = getCompanyLoginPermissions(sessionLogin);
    if (!permissions) {
      return deniedCompanyModulePermission(moduleName);
    }

    return (
      deriveCompanyPermissions(permissions).find(
        (permission) => permission?.module_name === moduleName,
      ) ?? deniedCompanyModulePermission(moduleName)
    );
  }

  const permissions = getCompanyLoginPermissions(sessionLogin);
  if (!permissions) return null;

  return (
    deriveCompanyPermissions(permissions).find(
      (permission) => permission?.module_name === moduleName,
    ) ?? deniedCompanyModulePermission(moduleName)
  );
};

/**
 * Check a single permission flag (`can_view`, `can_add`, `can_edit`, `can_delete`)
 * on a company module. Owners / legacy sessions without permissions data fail open.
 */
export const canCompanyModule = (moduleName, flag = "can_view", login) => {
  const permission = getCompanyModulePermission(moduleName, login);
  if (!permission) return true;
  return Boolean(permission[flag]);
};

export const canViewCompanyModules = (moduleNames, login) => {
  if (moduleNames == null) return true;
  const names = Array.isArray(moduleNames)
    ? moduleNames.filter(Boolean)
    : [moduleNames].filter(Boolean);
  if (!names.length) return true;
  return names.some((name) => canCompanyModule(name, "can_view", login));
};

export const canAddCompanyModule = (moduleName, login) =>
  canCompanyModule(moduleName, "can_add", login);

export const canEditCompanyModule = (moduleName, login) =>
  canCompanyModule(moduleName, "can_edit", login);

export const canDeleteCompanyModule = (moduleName, login) =>
  canCompanyModule(moduleName, "can_delete", login);

/** Primary company-portal destinations in sidebar order — the redirect walks
 * these and lands on the first one the employee can view. Mirrors the nav in
 * `SideMenu`. Keep in sync when a top-level module route is added/reordered. */
const companyPortalHomeCandidates = [
  { modules: companyPermissionModules.events, route: ROUTE_PATHS.companyEvents },
  {
    modules: companyPermissionModules.manageClients,
    route: ROUTE_PATHS.companyClients,
  },
  {
    modules: [
      companyPermissionModules.employees,
      companyPermissionModules.rolesManagement,
    ],
    route: ROUTE_PATHS.companyTeamsPermissions,
  },
  {
    modules: companyPermissionModules.applicants,
    route: ROUTE_PATHS.companyLoyalApplicants,
  },
];

/** Post-login / redirect target: the Dashboard for the company owner
 * (PROLINE-3699 — its overview endpoint is owner-only, so an employee would
 * just hit a 403 there), else Events when allowed, else the first viewable
 * module, else the no-permission empty state (zero-permission employee). */
export const getCompanyPortalHomeRoute = (login) => {
  if (isCompanyOwner(login)) return ROUTE_PATHS.companyDashboard;

  const candidate = companyPortalHomeCandidates.find(({ modules }) =>
    canViewCompanyModules(modules, login),
  );
  return candidate?.route ?? ROUTE_PATHS.companyNoPermission;
};

/**
 * Admin-portal RBAC, mirroring the company-portal section above. Source of
 * truth is the *fresh* `/admin_roles/:id/permissions` array in
 * `state.auth.permission` — never the stale `login.role.permissions` snapshot.
 */
const deniedAdminModulePermission = (moduleName) => ({
  module_name: moduleName,
  can_view: false,
  can_add: false,
  can_edit: false,
  can_delete: false,
});

const fullAdminModulePermission = (moduleName) => ({
  module_name: moduleName,
  can_view: true,
  can_add: true,
  can_edit: true,
  can_delete: true,
});

/** Supervisor Events row: view-only (route/menu admission), no CRUD. Actual
 * evaluation capability is governed separately, not through this matrix. */
const supervisorAdminModulePermission = (moduleName) =>
  moduleName === adminPermissionModules.events
    ? { ...deniedAdminModulePermission(moduleName), can_view: true }
    : deniedAdminModulePermission(moduleName);

/** Fresh admin-portal permissions array for the logged-in user (`[]` when absent). */
export const getAdminPermissions = () => store.getState().auth?.permission ?? [];

/** Permission row for one admin module. Super admin fails open (full row);
 * visitor reads the fresh array; supervisor gets the fixed Events-only row. */
export const getAdminModulePermission = (moduleName) => {
  if (isSuperAdmin()) {
    return fullAdminModulePermission(moduleName);
  }

  if (isSupervisorManger()) {
    return supervisorAdminModulePermission(moduleName);
  }

  if (isVisitor()) {
    const permissions = getAdminPermissions();
    return (
      permissions.find((permission) => permission?.module_name === moduleName) ??
      deniedAdminModulePermission(moduleName)
    );
  }

  return deniedAdminModulePermission(moduleName);
};

export const canAdminModule = (moduleName, flag = "can_view") =>
  Boolean(getAdminModulePermission(moduleName)[flag]);

export const canViewAdminModules = (moduleNames) => {
  if (moduleNames == null) return true;
  const names = Array.isArray(moduleNames)
    ? moduleNames.filter(Boolean)
    : [moduleNames].filter(Boolean);
  if (!names.length) return true;
  return names.some((name) => canAdminModule(name, "can_view"));
};

/** First `permission_url` the visitor can view, or the no-permission fallback. */
export const getAdminPermissionsHomeRoute = () => {
  const permissions = getAdminPermissions();
  const activeRoute = permissions.find((item) => Boolean(item?.can_view));
  return activeRoute?.permission_url ?? ROUTE_PATHS.noPermission;
};
